import type { DecisionBody, FeedbackRef, HypothesisBody, MaterialBindingBody, ResearchNextTest, ResearchRecord, ResearchRef, SourceStatus } from "../../../shared/wire/research.ts";
import { associationLabel, DISPOSITION_LABELS, sourceStatusLabel, TEST_STATUS_LABELS, timeLabel } from "./researchLabels";

/** The decisions-domain fields of a record (ADR-0090): route, reason, rejected routes, reconsideration conditions, tests. */
export function DecisionDetail({ record }: { record: ResearchRecord }) {
	return (
		<div className="research-decision">
			{record.kind === "decision" && <Decision body={record.body as unknown as DecisionBody} />}
			{record.kind === "hypothesis" && <Hypothesis body={record.body as unknown as HypothesisBody} />}
			{record.kind === "material_binding" && <Binding body={record.body as unknown as MaterialBindingBody} />}
			{record.confirmationActor && (
				<p className="research-meta">
					确认人 {record.confirmationActor} · r{record.confirmedRevision ?? record.revision}
					{record.confirmedAt ? ` · ${timeLabel(record.confirmedAt)}` : ""}
				</p>
			)}
			{record.refs.length > 0 && <Field label="引用" items={record.refs.map(refText)} />}
		</div>
	);
}

function Decision({ body }: { body: DecisionBody }) {
	return (
		<>
			<p>路线：{body.route}</p>
			<p>理由：{body.reason}</p>
			{body.alternatives.length > 0 && (
				<ul className="research-list">
					{body.alternatives.map((a, i) => (
						<li key={i}>
							备选 {a.route} · {DISPOSITION_LABELS[a.disposition] ?? a.disposition}
							{a.reason ? `：${a.reason}` : ""}
							{a.reconsiderWhen?.length ? ` · 重新考虑条件：${a.reconsiderWhen.join("；")}` : ""}
						</li>
					))}
				</ul>
			)}
			<Field label="证据" items={body.evidenceRefs.map(refText)} />
			<Field label="重新考虑条件" items={body.reconsiderWhen} />
			<Field label="待做检验" items={body.nextTests.map(testText)} />
		</>
	);
}

function Hypothesis({ body }: { body: HypothesisBody }) {
	return (
		<>
			<p>
				{body.statement} · {TEST_STATUS_LABELS[body.testStatus] ?? body.testStatus}
			</p>
			<Field label="检验材料" items={body.testRefs.map(refText)} />
			<Field label="证伪条件" items={body.falsificationConditions} />
			<Field label="待做检验" items={body.nextTests.map(testText)} />
		</>
	);
}

function Binding({ body }: { body: MaterialBindingBody }) {
	return (
		<>
			<p>
				{refText(body.materialRef)} · {associationLabel(body.associationStatus)}
			</p>
			<p>理由：{body.rationale}</p>
		</>
	);
}

function Field({ label, items }: { label: string; items: string[] }) {
	if (items.length === 0) return null;
	return (
		<p className="research-meta">
			{label}：{items.join("；")}
		</p>
	);
}

function testText(t: ResearchNextTest): string {
	return typeof t === "string" ? t : t.action;
}

function refText(given: ResearchRef | FeedbackRef | string): string {
	if (typeof given === "string") return given;
	const ref = given as ResearchRef;
	const named = [ref.path, ref.url, ref.key, ref.note, ref.id].find((v) => typeof v === "string" && v);
	const status = typeof ref.sourceStatus === "string" && ref.sourceStatus !== "current" ? ` · ${sourceStatusLabel(ref.sourceStatus as SourceStatus)}` : "";
	return `${ref.kind} ${String(named ?? "")}`.trim() + status;
}
